import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  Divider,
  Grid,
  Paper,
  Button,
} from '@mui/material';
import { 
  EmojiEvents, 
  TrendingUp, 
  CalendarToday,
  Star,
  Whatshot,
  Timeline 
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiConfig } from '../config/api';

const Leaderboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [allTimeLeaderboard, setAllTimeLeaderboard] = useState([]);
  const [weeklyLeaderboard, setWeeklyLeaderboard] = useState([]);
  const theme = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    fetchLeaderboards();
  }, []);

  const fetchLeaderboards = async () => {
    try {
      const [allTimeResponse, weeklyResponse] = await Promise.all([
        axios.get(apiConfig.leaderboard.overall),
        axios.get(apiConfig.leaderboard.weekly)
      ]);
      
      setAllTimeLeaderboard(allTimeResponse.data.leaderboard || []);
      setWeeklyLeaderboard(weeklyResponse.data || []);
    } catch (error) {
      setError('Tulostaulun lataus epäonnistui');
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleProfileClick = (userId) => {
    navigate(`/profile/${userId}`);
  };

  const getRankColor = (rank) => {
    switch (rank) {
      case 1: return theme.palette.warning.main; // Gold
      case 2: return '#C0C0C0'; // Silver
      case 3: return '#CD7F32'; // Bronze
      default: return theme.palette.text.primary;
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return `#${rank}`;
    }
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          Ladataan tulostaulua...
        </Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 6 }}>
        {/* Header */}
        <Box 
          textAlign="center" 
          mb={6}
          sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
            borderRadius: 4,
            p: 4,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <EmojiEvents 
            sx={{ 
              fontSize: 64, 
              color: 'warning.main', 
              mb: 2,
              filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))',
            }} 
          />
          <Typography 
            variant="h3" 
            gutterBottom 
            sx={{ 
              fontWeight: 700,
              background: `linear-gradient(45deg, ${theme.palette.warning.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            🏆 Tulostaulu
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Katso ketkä ovat parhaat ennustajat ja kilpaile pisteytyksessä!
          </Typography>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 4, 
              borderRadius: 2,
              '& .MuiAlert-message': {
                fontSize: '1rem',
              },
            }}
          >
            {error}
          </Alert>
        )}

        {/* Statistics Cards */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={4}>
            <Paper 
              sx={{ 
                p: 3, 
                textAlign: 'center',
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Star sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {allTimeLeaderboard.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Aktiivista Pelaajaa
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper 
              sx={{ 
                p: 3, 
                textAlign: 'center',
                background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
              }}
            >
              <Whatshot sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                {weeklyLeaderboard.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Viikon Ennustajaa
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper 
              sx={{ 
                p: 3, 
                textAlign: 'center',
                background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              }}
            >
              <Timeline sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                {allTimeLeaderboard.reduce((sum, user) => sum + (user.totalPredictions || 0), 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ennustusta Yhteensä
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Card 
          sx={{ 
            borderRadius: 4,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            indicatorColor="primary"
            textColor="primary"
            sx={{
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              '& .MuiTab-root': {
                py: 2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
              },
            }}
          >
            <Tab 
              icon={<TrendingUp />} 
              label="Kauden Tilanne" 
              iconPosition="start"
            />
            <Tab 
              icon={<CalendarToday />} 
              label="Viikon Parhaat" 
              iconPosition="start"
            />
          </Tabs>

          <Divider />

          <CardContent sx={{ p: 0 }}>
            {tabValue === 0 && (
              <List sx={{ py: 2 }}>
                {allTimeLeaderboard.map((user, index) => (
                  <ListItem
                    key={user.id || user._id}
                    sx={{
                      py: 2,
                      px: 3,
                      bgcolor: index < 3 ? alpha(getRankColor(user.rank), 0.1) : 'transparent',
                      borderRadius: 0,
                      borderBottom: index < allTimeLeaderboard.length - 1 ? `1px solid ${alpha(theme.palette.divider, 0.1)}` : 'none',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.action.hover, 0.5),
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar 
                        src={user.avatarUrl || user.avatar_url}
                        sx={{ 
                          width: 50,
                          height: 50,
                          bgcolor: getRankColor(user.rank),
                          color: user.rank <= 3 ? 'white' : 'inherit',
                          border: user.rank <= 3 ? `3px solid ${getRankColor(user.rank)}` : 'none',
                          boxShadow: user.rank <= 3 ? '0 4px 12px rgba(0,0,0,0.2)' : 'none',
                        }}
                      >
                        {user.name?.[0] || 'U'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={2}>
                          <Typography 
                            variant="h5" 
                            sx={{ 
                              minWidth: 60,
                              fontWeight: 800,
                              color: getRankColor(user.rank),
                              textShadow: user.rank <= 3 ? '0 2px 4px rgba(0,0,0,0.2)' : 'none',
                            }}
                          >
                            {getRankIcon(user.rank)}
                          </Typography>
                          <Typography variant="h6" 
                            component="button"
                            onClick={() => handleProfileClick(user.id || user._id)}
                            sx={{ 
                              fontWeight: 600,
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: theme.palette.primary.main,
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            {user.name}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box display="flex" gap={1} mt={1.5} flexWrap="wrap">
                          <Chip 
                            label={`${user.totalPoints} pistettä`} 
                            size="small" 
                            color="primary" 
                            variant="filled"
                            sx={{ fontWeight: 600 }}
                          />
                          <Chip 
                            label={`${user.accuracy}% tarkkuus`} 
                            size="small" 
                            color="secondary"
                            variant="filled"
                            sx={{ fontWeight: 600 }}
                          />
                          <Chip 
                            label={`${user.correctPredictions}/${user.totalPredictions} oikein`} 
                            size="small" 
                            variant="outlined"
                            sx={{ 
                              borderColor: alpha(theme.palette.text.primary, 0.3),
                              fontWeight: 500,
                            }}
                          />
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
                {allTimeLeaderboard.length === 0 && (
                  <Box textAlign="center" py={8}>
                    <EmojiEvents sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Ei vielä ennustuksia
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Ole ensimmäinen tekemään ennustuksia!
                    </Typography>
                  </Box>
                )}
              </List>
            )}

            {tabValue === 1 && (
              <List sx={{ py: 2 }}>
                {weeklyLeaderboard.map((user, index) => (
                  <ListItem
                    key={user.id || user._id}
                    sx={{
                      py: 2,
                      px: 3,
                      bgcolor: index < 3 ? alpha(getRankColor(index + 1), 0.1) : 'transparent',
                      borderRadius: 0,
                      borderBottom: index < weeklyLeaderboard.length - 1 ? `1px solid ${alpha(theme.palette.divider, 0.1)}` : 'none',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.action.hover, 0.5),
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar 
                        src={user.avatarUrl || user.avatar_url}
                        sx={{ 
                          width: 50,
                          height: 50,
                          bgcolor: getRankColor(index + 1),
                          color: index < 3 ? 'white' : 'inherit',
                          border: index < 3 ? `3px solid ${getRankColor(index + 1)}` : 'none',
                          boxShadow: index < 3 ? '0 4px 12px rgba(0,0,0,0.2)' : 'none',
                        }}
                      >
                        {user.name?.[0] || 'U'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={2}>
                          <Typography 
                            variant="h5" 
                            sx={{ 
                              minWidth: 60,
                              fontWeight: 800,
                              color: getRankColor(index + 1),
                              textShadow: index < 3 ? '0 2px 4px rgba(0,0,0,0.2)' : 'none',
                            }}
                          >
                            {getRankIcon(index + 1)}
                          </Typography>
                          <Typography variant="h6" 
                            component="button"
                            onClick={() => handleProfileClick(user.id || user._id)}
                            sx={{ 
                              fontWeight: 600,
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: theme.palette.primary.main,
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            {user.name}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box display="flex" gap={1} mt={1.5} flexWrap="wrap">
                          <Chip 
                            label={`${user.weeklyPoints} pistettä tällä viikolla`} 
                            size="small" 
                            color="primary" 
                            variant="filled"
                            sx={{ fontWeight: 600 }}
                          />
                          <Chip 
                            label={`${user.weeklyCorrect}/${user.weeklyPredictions} oikein`} 
                            size="small" 
                            variant="outlined"
                            sx={{ 
                              borderColor: alpha(theme.palette.text.primary, 0.3),
                              fontWeight: 500,
                            }}
                          />
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
                {weeklyLeaderboard.length === 0 && (
                  <Box textAlign="center" py={8}>
                    <CalendarToday sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Ei viikon ennustuksia
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Tee ennustuksia tämän viikon otteluista!
                    </Typography>
                  </Box>
                )}
              </List>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Leaderboard;
